import { AppConfig } from "../config/appConfig";

/**
 * Class for inter-instance communication and coordination.
 */
export class ApplicationHub {
	private static _instance: ApplicationHub = new ApplicationHub();
	private _instanceId: string;
	get instanceId(): string { return this._instanceId; }
	private _broadcastChannel?: BroadcastChannel;
	private _messageSubscriptions: IMessageSubscription[] = [];
	get getSubscriptions(): IMessageSubscription[] { return this._messageSubscriptions; }
	private _debugLogging: boolean = false;

	/** Get the singleton instance of the Application Hub. */
	public static getInstance(): ApplicationHub {
		return ApplicationHub._instance;
	}

	private constructor() {
		this._instanceId = crypto.randomUUID();
		this._broadcastChannel = new BroadcastChannel(AppConfig.appHubBroadcastChannelName);
		this._broadcastChannel.onmessage = (event) => this._handleBroadcastMessage(event);
		this.logToConsole(`Initialized with Instance ID: ${this._instanceId}`);
	}

	/**
	 * Send the provided message and data to the console if debug logging is enabled.
	 * @param message The message to log.
	 * @param data Optional accompanying data.
	 */
	private logToConsole(message: string, ...data: any[]): void {
		if (this._debugLogging) {
			console.log(`[${new Date().toISOString()}] ApplicationHub: ${message}`, ...data);
		}
	}

	/** 
	 * Broadcast a message to all application instances. 
	 * @param message The message to broadcast.
	 * */
	broadcast = (subject: ApplicationHubMessageSubject, payload?: any): void => {
		const message = {
			senderInstanceId: this._instanceId,
			timestamp: new Date(),
			subject: subject,
			payload: payload
		} as IApplicationHubMessage;
		this._broadcastChannel?.postMessage(message);
		this.logToConsole(`Message broadcast from Instance ID: ${this._instanceId}`, message);
	};

	/** 
	 * Subscribe to messages sent via the Application Hub. 
	 * @param subscription The subscription details including the handler and subjects to listen for.
	 * */
	subscribeToMessages = (subscription: IMessageSubscription): void => {
		const currentSubscription = this._messageSubscriptions.find(sub => sub.name === subscription.name);
		if (currentSubscription) {
			currentSubscription.handler = subscription.handler;
			currentSubscription.subjects = subscription.subjects;
			this.logToConsole(`Updated existing "${currentSubscription.name}" subscription.`, currentSubscription);
		}
		else {
			this._messageSubscriptions.push(subscription);
			this.logToConsole(`New "${subscription.name}" subscription added.`, subscription);
		}
	}

	/**
	 * Unsubscribe from messages sent via the Application Hub.
	 * @param name The name of the subscription to remove, provided when subscribing.
	 */
	unsubscribeFromMessages = (name: string): void => {
		const currentSubscription = this._messageSubscriptions.find(sub => sub.name === name);
		if (currentSubscription) {
			this._messageSubscriptions = this._messageSubscriptions.filter(sub => sub.name !== name);
			this.logToConsole(`Removed "${name}" subscription.`, currentSubscription);
		}
		else {
			this.logToConsole(`No existing "${name}" subscription found to remove.`);
		}
	}

	/**
	 * Deliver an incoming broadcast message to all subscribed handlers.
	 * @param event The message event from the broadcast channel.
	 */
	private _handleBroadcastMessage = (event: MessageEvent): void => {
		this.logToConsole(`Delivering broadcast message to ${this._messageSubscriptions.length} subscriber(s).`);
		const hubMessage: IApplicationHubMessage = event.data;
		this._messageSubscriptions.forEach(sub => {
			if ((sub.subjects ?? []).length == 0 || sub.subjects.includes(hubMessage.subject)) {
				try {
					sub.handler(hubMessage);
				} catch (error) {
					// If there's an error distributing to one subscriber, log it but continue to the next
					this.logToConsole(`Error relaying message to subscriber "${sub?.name ?? "N/A"}": ${error}`);
				}
			}
		});
	}
}

/** A subscription providing a callback method to receive messages sent via the Application Hub. */
export interface IMessageSubscription {

	/** Subscription name. Used to identify subscriptions for update/removal. */
	name: string;

	/** A function that handles the incoming message. */
	handler: (message: IApplicationHubMessage) => void;

	/** The subjects of messages to listen for. If not provided, all messages will be received. */
	subjects?: ApplicationHubMessageSubject[];
}

/** An update claim represents a temporary claim an application instance has made to update a shared store resource to prevent duplicate updates. */
export class UpdateClaim {

	/** The type of resource being updated. */
	type: UpdateClaimType;

	/** The ID of the application instance claiming the authority to update. */
	instanceId: string;

	/** The date the claim was created. */
	created: Date;

	/** The date the claim was last modified. */
	modified?: Date;

	/** Any additional data associated with the claim. Optional. */
	data?: any;

	/** Whether the claim has expired, based on its last modified date or creation date if it has not been modified. */
	get hasExpired() {
		const yieldMs = AppConfig.idbUpdateClaimYieldDurationMs;
		const now = new Date();
		return (!this.modified && (now.getTime() - this.created.getTime() > yieldMs)) ||
			(this.modified && (now.getTime() - this.modified.getTime() > yieldMs));
	}

	/**
	 * Create a new Update Claim.
	 * @param type The type of resource being updated.
	 * @param data Any additional data associated with the claim. Optional.
	 */
	constructor(type?: UpdateClaimType, data?: any) {
		this.type = type;
		this.instanceId = ApplicationHub.getInstance().instanceId;
		this.created = new Date();
		this.modified = undefined;
		this.data = data;
	}
}

/** The types of update claims that can be made. */
export enum UpdateClaimType {

	/** Request a new the access token for CredentialManager */
	AccessToken = "AccessToken"
}

/** A message sent between application instances via the Application Hub. */
export interface IApplicationHubMessage {

	/** The ID of the application instance sending the message. */
	senderInstanceId: string;

	/** The date and time the message was sent. */
	timestamp: Date;

	/** The subject of the message. */
	subject: ApplicationHubMessageSubject;

	/** An optional payload containing additional data for the message. */
	payload?: any;
}

/** The subjects of messages sent between application instances via the Application Hub. */
export enum ApplicationHubMessageSubject {

	/** A new access token is available for CredentialManager */
	NewAccessToken = "NewAccessToken"
}